/**
 * Company list page with search, filter, sort, and comparison selection.
 *
 * Uses client-side filtering via useCompanyListFilters for instant responsiveness.
 * AC-001: Selectable checkboxes on each company card
 * AC-002: "Compare" button navigates to comparison page
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompanies } from '../hooks/useCompanies';
import { useCompanyListFilters } from '../hooks/useCompanyListFilters';
import { CompanyCard } from '../components/company/CompanyCard';
import { CompanyListToolbar } from '../components/company/CompanyListToolbar';
import { useAuth } from '../contexts/AuthContext';

const MAX_SELECTION = 3;

export function CompanyListPage() {
  const { isAuthenticated } = useAuth();
  const { data: companies, isLoading, error } = useCompanies({ enabled: isAuthenticated });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const toggleSelection = useCallback((companyId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(companyId)) {
        next.delete(companyId);
      } else if (next.size < MAX_SELECTION) {
        next.add(companyId);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleCompare = useCallback(() => {
    const ids = Array.from(selectedIds).join(',');
    navigate(`/companies/compare?ids=${ids}`);
  }, [selectedIds, navigate]);

  const {
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    filterType,
    setFilterType,
    filterStatus,
    setFilterStatus,
    filteredCompanies,
    resultCount,
    totalCount,
    hasActiveFilters,
    clearFilters,
  } = useCompanyListFilters(companies ?? []);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 w-48 bg-gray-200 rounded mb-6"></div>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="h-40 bg-gray-200 rounded-lg"></div>
            <div className="h-40 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading companies</h3>
              <div className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'Unknown error'}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
        <p className="mt-1 text-sm text-gray-500">Tracked companies with knowledge base status</p>
      </div>

      {/* Search, Sort & Filter Toolbar */}
      <CompanyListToolbar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortBy={sortBy}
        onSortByChange={setSortBy}
        sortOrder={sortOrder}
        onSortOrderChange={setSortOrder}
        filterType={filterType}
        onFilterTypeChange={setFilterType}
        filterStatus={filterStatus}
        onFilterStatusChange={setFilterStatus}
        resultCount={resultCount}
        totalCount={totalCount}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
      />

      {/* Company grid with comparison selection */}
      {filteredCompanies.length > 0 ? (
        <div className="grid gap-6 md:grid-cols-2">
          {filteredCompanies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              isSelected={selectedIds.has(company.id)}
              onToggleSelect={toggleSelection}
              selectionDisabled={!selectedIds.has(company.id) && selectedIds.size >= MAX_SELECTION}
            />
          ))}
        </div>
      ) : totalCount === 0 ? (
        /* No companies tracked at all */
        <div className="text-center py-12">
          <p className="text-gray-500">No companies tracked yet.</p>
        </div>
      ) : (
        /* Companies exist but all filtered out (AC-004) */
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">No companies match your filters.</p>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* AC-001: Sticky action bar when 2+ companies selected */}
      {selectedIds.size >= 2 && (
        <div
          className="fixed bottom-0 left-0 right-0 z-40 border-t border-gray-200 bg-white px-4 py-3 shadow-lg"
          data-testid="compare-action-bar"
        >
          <div className="mx-auto max-w-7xl flex items-center justify-between">
            <span className="text-sm text-gray-600">{selectedIds.size} companies selected</span>
            <div className="flex items-center gap-3">
              <button
                onClick={clearSelection}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                data-testid="clear-selection-button"
              >
                Clear
              </button>
              <button
                onClick={handleCompare}
                className="inline-flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
                data-testid="compare-button"
              >
                Compare
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CompanyListPage;
